import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CloseIcon,
  DeleteIcon,
  DragHandleIcon,
  FilterTypeEventCountIcon,
  FilterTypeEventIcon,
  FilterTypeFieldExistIcon,
  FilterTypeSequenceIcon,
  FilterTypeTimeBasedIcon,
  FilterTypeUserHistoryIcon,
  FilterTypeUserIcon,
  PlusIcon,
} from 'app/common/presentation/icons'
import { ObjectiveFilterOperator } from 'app/objectives/types'

import { Button, DropDown, IconButton, Input, NumberSelector } from 'components'

import styles from './FilterRow.module.scss'
import {
  FilterDraft,
  FilterDraftType,
  PropertyFilterDraft,
  createFilterDraft,
  isNumericFilterOperator,
  numericOperators,
} from './filter'

const stringOperators: ObjectiveFilterOperator[] = ['contains', 'equals']
const occurrenceOperators: ObjectiveFilterOperator[] = [
  'greaterThan',
  'greaterThanOrEqual',
  'equals',
  'lessThan',
  'lessThanOrEqual',
]

interface FilterRowProps {
  dragging?: boolean
  filter: FilterDraft
  options: string[]
  onChange: (filter: FilterDraft) => void
  onDragEnd: () => void
  onDragStart: () => void
  onDrop: () => void
  onRemove: () => void
}

const renderTypeIcon = (type: FilterDraftType): ReactNode => {
  switch (type) {
    case 'event':
      return <FilterTypeEventIcon />
    case 'user':
      return <FilterTypeUserIcon />
    case 'userHistory':
      return <FilterTypeUserHistoryIcon />
    case 'eventCount':
      return <FilterTypeEventCountIcon />
    case 'timeBased':
      return <FilterTypeTimeBasedIcon />
    case 'sequence':
      return <FilterTypeSequenceIcon />
    case 'fieldExist':
      return <FilterTypeFieldExistIcon />
  }
}

const FilterRow = ({
  dragging,
  filter,
  onChange,
  onDragEnd,
  onDragStart,
  onDrop,
  onRemove,
  options,
}: FilterRowProps) => {
  const { t } = useTranslation('objectives')

  const typeOptions: FilterDraftType[] = [
    'event',
    'user',
    'userHistory',
    'eventCount',
    'timeBased',
    'sequence',
    'fieldExist',
  ]

  const typeDropDown = (
    <DropDown
      className={`${styles.dropDown} ${styles.typeDropDown}`}
      classNameMenu={styles.typeMenu}
      data={typeOptions.map((type) => ({
        icon: renderTypeIcon(type),
        label: t(`create.filters.types.${type}`),
        value: type,
      }))}
      onSelect={(type) => onChange(createFilterDraft(filter.id, type))}
      selected={filter.type}
      small
    />
  )

  const optionDropDown = (
    value: string,
    onSelect: (value: string) => void,
    placeholder: string,
  ) => (
    <DropDown
      className={`${styles.dropDown} ${styles.optionDropDown}`}
      classNameMenu={styles.optionMenu}
      data={options}
      onSelect={onSelect}
      placeholder={placeholder}
      selected={value || null}
      small
    />
  )

  const operatorDropDown = (
    operator: ObjectiveFilterOperator,
    operators: ObjectiveFilterOperator[],
    onSelect: (operator: ObjectiveFilterOperator) => void,
    translationPath = 'create.filters.operators',
  ) => (
    <DropDown
      className={`${styles.dropDown} ${styles.operatorDropDown}`}
      classNameMenu={styles.operatorMenu}
      data={operators.map((value) => ({
        label: t(`${translationPath}.${value}`),
        value,
      }))}
      onSelect={onSelect}
      selected={operator}
      small
    />
  )

  const renderPropertyFilter = (propertyFilter: PropertyFilterDraft) => {
    const operators = [...stringOperators, ...numericOperators]
    const numeric = isNumericFilterOperator(propertyFilter.operator)

    return (
      <>
        {optionDropDown(
          propertyFilter.property,
          (property) => onChange({ ...propertyFilter, property }),
          t('create.filters.fields.property'),
        )}
        {operatorDropDown(propertyFilter.operator, operators, (operator) =>
          onChange({ ...propertyFilter, operator, value: '' }),
        )}
        <Input
          className={styles.valueInput}
          key={numeric ? 'number' : 'text'}
          onChange={(value) => onChange({ ...propertyFilter, value })}
          placeholder={t('create.filters.fields.value')}
          small
          type={numeric ? 'number' : 'text'}
          value={propertyFilter.value}
        />
      </>
    )
  }

  const renderControls = () => {
    switch (filter.type) {
      case 'event':
      case 'user':
        return renderPropertyFilter(filter)
      case 'fieldExist':
        return (
          <>
            {optionDropDown(
              filter.property,
              (property) => onChange({ ...filter, property }),
              t('create.filters.fields.property'),
            )}
            <span className={styles.staticValue}>{t('create.filters.operators.exists')}</span>
          </>
        )
      case 'timeBased':
        return (
          <>
            {optionDropDown(
              filter.property,
              (property) => onChange({ ...filter, property }),
              t('create.filters.fields.property'),
            )}
            <span className={styles.staticValue}>
              {t('create.filters.operators.withinLastDays')}
            </span>
            <NumberSelector
              min={0}
              onChange={(value) => onChange({ ...filter, value })}
              value={filter.value}
            />
            <span className={styles.unit}>{t('create.filters.units.days')}</span>
          </>
        )
      case 'eventCount':
        return (
          <>
            {optionDropDown(
              filter.event,
              (event) => onChange({ ...filter, event }),
              t('create.filters.fields.event'),
            )}
            <span className={styles.unit}>{t('create.filters.occurrences.occurred')}</span>
            {operatorDropDown(
              filter.operator,
              occurrenceOperators,
              (operator) => onChange({ ...filter, operator }),
              'create.filters.occurrences.operators',
            )}
            <NumberSelector
              min={0}
              onChange={(value) => onChange({ ...filter, value })}
              value={filter.value}
            />
            <span className={styles.unit}>{t('create.filters.units.times')}</span>
          </>
        )
      case 'userHistory':
        return (
          <div className={styles.history}>
            <div className={styles.historyOccurrence}>
              {typeDropDown}
              {optionDropDown(
                filter.event,
                (event) => onChange({ ...filter, event }),
                t('create.filters.fields.event'),
              )}
              <span className={styles.unit}>{t('create.filters.occurrences.occurred')}</span>
              {operatorDropDown(
                filter.operator,
                occurrenceOperators,
                (operator) => onChange({ ...filter, operator }),
                'create.filters.occurrences.operators',
              )}
              <NumberSelector
                min={0}
                onChange={(value) => onChange({ ...filter, value })}
                value={filter.value}
              />
              <span className={styles.unit}>{t('create.filters.units.times')}</span>
            </div>

            <div className={styles.conditionList}>
              {filter.conditions.map((condition, index) => (
                <div className={styles.condition} key={index}>
                  <span className={styles.conditionGuide} />
                  <span className={styles.conditionJoin}>
                    {index === 0
                      ? t('create.filters.condition.where')
                      : t('create.filters.condition.and')}
                  </span>
                  {optionDropDown(
                    condition.property,
                    (property) =>
                      onChange({
                        ...filter,
                        conditions: filter.conditions.map((current, conditionIndex) =>
                          conditionIndex === index ? { ...current, property } : current,
                        ),
                      }),
                    t('create.filters.fields.property'),
                  )}
                  {operatorDropDown(
                    condition.operator,
                    [...stringOperators, ...numericOperators],
                    (operator) =>
                      onChange({
                        ...filter,
                        conditions: filter.conditions.map((current, conditionIndex) =>
                          conditionIndex === index ? { ...current, operator, value: '' } : current,
                        ),
                      }),
                  )}
                  <Input
                    className={`${styles.valueInput} ${styles.conditionValue}`}
                    onChange={(value) =>
                      onChange({
                        ...filter,
                        conditions: filter.conditions.map((current, conditionIndex) =>
                          conditionIndex === index ? { ...current, value } : current,
                        ),
                      })
                    }
                    placeholder={t('create.filters.fields.value')}
                    small
                    type={isNumericFilterOperator(condition.operator) ? 'number' : 'text'}
                    value={condition.value}
                  />
                  <IconButton
                    aria-label={t('create.filters.removeCondition')}
                    className={styles.conditionRemove}
                    icon={CloseIcon}
                    onClick={() =>
                      onChange({
                        ...filter,
                        conditions: filter.conditions.filter(
                          (_, conditionIndex) => conditionIndex !== index,
                        ),
                      })
                    }
                    small
                    variant="tertiary"
                  />
                </div>
              ))}

              <div className={styles.addConditionRow}>
                <span className={styles.conditionGuide} />
                <Button
                  onClick={() =>
                    onChange({
                      ...filter,
                      conditions: [
                        ...filter.conditions,
                        { operator: 'equals', property: '', value: '' },
                      ],
                    })
                  }
                  rightIcon={<PlusIcon />}
                  small
                  variant="tertiary"
                >
                  {t('create.filters.addCondition')}
                </Button>
              </div>
            </div>
          </div>
        )
      case 'sequence':
        return (
          <div className={styles.sequence}>
            <div className={styles.sequenceHeader}>{typeDropDown}</div>
            <div className={styles.sequenceList}>
              {filter.steps.map((step, index) => (
                <Fragment key={index}>
                  <div className={styles.sequenceStep}>
                    <span className={styles.conditionGuide} />
                    <span className={styles.stepNumber}>{index + 1}</span>
                    <DropDown
                      className={styles.dropDown}
                      classNameMenu={styles.sequenceActionMenu}
                      data={[
                        { label: t('create.filters.sequence.did'), value: false },
                        { label: t('create.filters.sequence.didNot'), value: true },
                      ]}
                      onSelect={(negate) =>
                        onChange({
                          ...filter,
                          steps: filter.steps.map((current, stepIndex) =>
                            stepIndex === index ? { ...current, negate } : current,
                          ),
                        })
                      }
                      selected={step.negate}
                      small
                    />
                    {optionDropDown(
                      step.event,
                      (event) =>
                        onChange({
                          ...filter,
                          steps: filter.steps.map((current, stepIndex) =>
                            stepIndex === index ? { ...current, event } : current,
                          ),
                        }),
                      t('create.filters.fields.event'),
                    )}
                    {index >= 2 && (
                      <IconButton
                        aria-label={t('create.filters.removeStep')}
                        className={styles.conditionRemove}
                        icon={CloseIcon}
                        onClick={() =>
                          onChange({
                            ...filter,
                            steps: filter.steps.filter((_, stepIndex) => stepIndex !== index),
                          })
                        }
                        small
                        variant="tertiary"
                      />
                    )}
                  </div>
                  {index < filter.steps.length - 1 && (
                    <div className={styles.sequenceThen}>
                      <span className={styles.conditionGuide} />
                      <span>{t('create.filters.sequence.then')}</span>
                    </div>
                  )}
                </Fragment>
              ))}
              <div className={styles.addSequenceRow}>
                <span className={styles.conditionGuide} />
                <Button
                  onClick={() =>
                    onChange({
                      ...filter,
                      steps: [...filter.steps, { event: '', negate: false }],
                    })
                  }
                  rightIcon={<PlusIcon />}
                  small
                  variant="tertiary"
                >
                  {t('create.filters.addStep')}
                </Button>
              </div>
            </div>
          </div>
        )
    }
  }

  return (
    <div
      className={`${styles.filterRow} ${['sequence', 'userHistory'].includes(filter.type) ? styles.expanded : ''} ${dragging ? styles.dragging : ''}`}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        onDrop()
      }}
    >
      <button
        aria-label={t('create.filters.reorder')}
        className={styles.dragHandle}
        draggable
        onDragEnd={onDragEnd}
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = 'move'
          event.dataTransfer.setData('text/plain', String(filter.id))
          event.dataTransfer.setDragImage(event.currentTarget.parentElement!, 13, 12)
          onDragStart()
        }}
        type="button"
      >
        <DragHandleIcon />
      </button>

      <div className={styles.controls}>
        {!['sequence', 'userHistory'].includes(filter.type) && typeDropDown}
        {renderControls()}
      </div>

      <span className={styles.removeCell}>
        <IconButton
          aria-label={t('create.filters.remove')}
          className={styles.removeButton}
          icon={DeleteIcon}
          onClick={onRemove}
          small
          variant="tertiary"
        />
      </span>
    </div>
  )
}

export default FilterRow
